import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api.js';
import { useBackgroundJobs } from '../jobs/BackgroundJobsContext';

/**
 * ChecklistImportModal — import a document as the discovery checklist with a
 * live, see-before-you-replace preview.
 *
 * Replaces the old inline confirm strip (which clipped its Import button off
 * the right edge). On open and whenever the parse mode changes, we run a
 * server-side dry-run (`role` endpoint with `dry_run: true`) that parses the
 * document WITHOUT persisting, so the user sees the would-be item count + a
 * sample and the number of existing items being replaced before committing.
 *
 * Parse mode maps to the backend `permissive` flag:
 *   'auto'  → permissive:false  (detect bullets / numbers / table / CSV)
 *   'lines' → permissive:true   (every non-empty line is an item)
 */
export default function ChecklistImportModal({
  caseId, doc, isChecklist, onClose, onImported,
}) {
  const { submitJob } = useBackgroundJobs();
  const [mode, setMode] = useState('auto'); // 'auto' | 'lines'
  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);

  const permissive = mode === 'lines';

  const fetchPreview = useCallback(async (signal) => {
    setPhase('loading');
    setError(null);
    try {
      const res = await api.post(`/cases/${caseId}/documents/${doc.id}/role`, {
        role: 'discovery_checklist',
        permissive,
        dry_run: true,
      });
      if (signal?.aborted) return;
      setPreview(res);
      setPhase('ready');
    } catch (e) {
      if (signal?.aborted) return;
      setError(e.message || 'Could not preview this document.');
      setPhase('error');
    }
  }, [caseId, doc.id, permissive]);

  useEffect(() => {
    const ctrl = { aborted: false };
    fetchPreview(ctrl);
    return () => { ctrl.aborted = true; };
  }, [fetchPreview]);

  async function handleImport() {
    setImporting(true);
    setError(null);
    try {
      // Commit runs as a background job (fire-and-forget): submit, toast, close.
      // The dry-run preview above stays synchronous (fast + interactive). The
      // BackgroundJobsBanner shows progress; completion toasts "View checklist".
      const res = await submitJob(
        'import_checklist',
        api.post(`/cases/${caseId}/documents/${doc.id}/checklist-import`, { permissive }),
      );
      onImported?.(res);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Could not start the import.');
      setImporting(false);
    }
  }

  const wouldCreate = preview?.would_create ?? 0;
  const sample = preview?.items_preview || [];
  const formatError = preview?.format_error;
  const activeName = preview?.active_checklist_name;
  const newName = preview?.new_checklist_name;
  const sameNameArchive = preview?.same_name_archive;
  const wouldArchive = preview?.would_archive;
  const canImport = phase === 'ready' && wouldCreate > 0 && !importing;

  return createPortal((
    <div className="np-export-overlay" onClick={importing ? undefined : onClose}>
      <div className="np-export-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="np-export-head">
          <h3>{isChecklist ? 'Re-import as Discovery Checklist' : 'Import as Discovery Checklist'}</h3>
          <button className="np-icon-btn" onClick={onClose} title="Close" disabled={importing}>✕</button>
        </div>

        <div className="np-export-body" style={{ display: 'block', padding: 16 }}>
          <p style={{ marginTop: 0, fontSize: 13, color: '#555' }}>
            Source: <b>{doc.filename || 'document'}</b>
          </p>

          {phase === 'ready' && sameNameArchive && (
            <div className="cl-import-note">
              ↻ Re-imports <b>{newName}</b>. The previous version
              {wouldArchive ? ` (${wouldArchive} item${wouldArchive === 1 ? '' : 's'})` : ''}
              {' '}is archived — kept for audit, hidden from the picker.
            </div>
          )}
          {phase === 'ready' && !sameNameArchive && activeName && (
            <div className="cl-import-note">
              Adds a new active checklist. Your current checklist <b>{activeName}</b> stays
              in this case — switch anytime from the checklist picker. Nothing is deleted.
            </div>
          )}

          <div className="cl-import-section">
            <div className="cl-import-section-label">Read the list as</div>
            <label className="cl-import-radio">
              <input
                type="radio" name="cl-parse-mode"
                checked={mode === 'auto'}
                onChange={() => setMode('auto')}
                disabled={importing}
              />
              <span>Auto-detect list <span className="cl-import-hint">— bullets · numbers · table · CSV</span></span>
            </label>
            <label className="cl-import-radio">
              <input
                type="radio" name="cl-parse-mode"
                checked={mode === 'lines'}
                onChange={() => setMode('lines')}
                disabled={importing}
              />
              <span>Every non-empty line is an item <span className="cl-import-hint">— for plain lists with no bullets or numbers</span></span>
            </label>
          </div>

          <div className="cl-import-section">
            <div className="cl-import-section-label">
              {phase === 'loading' && 'Preview — reading the document…'}
              {phase === 'ready' && !formatError && `Preview — ${wouldCreate} item${wouldCreate === 1 ? '' : 's'} detected`}
              {phase === 'ready' && formatError && 'Preview'}
              {phase === 'error' && 'Preview'}
            </div>

            {phase === 'loading' && (
              <div className="cl-import-preview cl-import-preview--muted">Reading the document for list items — a few seconds for a short document.</div>
            )}

            {phase === 'ready' && formatError && (
              <div className="cl-import-preview cl-import-preview--muted">
                No list structure was detected.
                {mode === 'auto' && (
                  <> Try <button
                    className="cl-import-linkbtn"
                    onClick={() => setMode('lines')}
                    disabled={importing}
                  >Every non-empty line is an item</button>.</>
                )}
              </div>
            )}

            {phase === 'ready' && preview?.source_note && (
              <div className="cl-import-hint" style={{ marginBottom: 6 }}>{preview.source_note}</div>
            )}

            {phase === 'ready' && !formatError && (
              <ol className="cl-import-preview">
                {sample.map((label, i) => (
                  <li key={i} title={label}>{label}</li>
                ))}
                {wouldCreate > sample.length && (
                  <li className="cl-import-more">… {wouldCreate - sample.length} more</li>
                )}
              </ol>
            )}

            {phase === 'error' && <div className="bulk-popover-error">{error}</div>}
          </div>

          {error && phase !== 'error' && <div className="bulk-popover-error">{error}</div>}
        </div>

        <div className="np-export-foot">
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose} disabled={importing}>Cancel</button>
          <button className="btn btn-primary" onClick={handleImport} disabled={!canImport}>
            {importing ? 'Starting…' : (wouldCreate > 0 ? `Import ${wouldCreate} item${wouldCreate === 1 ? '' : 's'}` : 'Import')}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
